(function () {
  const SWIPER_ATTR = 'data-image-cards-swiper-initialized';

  function parseBool(value) {
    return value === 'true' || value === true;
  }

  function getMaxSlidesForWidth(sectionEl) {
    const w = window.innerWidth || 0;
    if (w >= 1024) return parseInt(sectionEl.dataset.slidesDesktop || '4', 10) || 4;
    if (w >= 768) return parseInt(sectionEl.dataset.slidesTablet || '3', 10) || 3;
    return Math.max(1, Math.round(parseFloat(sectionEl.dataset.slidesMobile || '1.2')));
  }

  function syncSlideWidth(sectionEl, swiper) {
    const slidesMode = sectionEl.dataset.slidesMode || 'fixed';
    const baseSlideWidth = parseInt(sectionEl.dataset.slideWidth || '280', 10) || 280;
    const gap = parseInt(sectionEl.dataset.spaceBetween || '0', 10);

    if (!swiper || !swiper.width) return;

    if (slidesMode !== 'fixed') {
      sectionEl.style.setProperty('--ic-slide-width', `${baseSlideWidth}px`);
      swiper.update();
      return;
    }

    const max = getMaxSlidesForWidth(sectionEl);
    const fit = Math.floor((swiper.width + gap) / (baseSlideWidth + gap));

    if (fit > max) {
      const fittedWidth = (swiper.width - gap * (max - 1)) / max;
      sectionEl.style.setProperty('--ic-slide-width', `${Math.floor(fittedWidth)}px`);
    } else {
      sectionEl.style.setProperty('--ic-slide-width', `${baseSlideWidth}px`);
    }

    swiper.update();
  }

  function destroyImageCardsSwipers(root) {
    const scope = root || document;
    scope.querySelectorAll(`[data-image-cards-slider][${SWIPER_ATTR}]`).forEach((slider) => {
      if (slider.__imageCardsSwiper) {
        slider.__imageCardsSwiper.destroy(true, true);
        slider.__imageCardsSwiper = null;
      }
      slider.removeAttribute(SWIPER_ATTR);
    });
  }

  function bindSectionResize(sectionEl) {
    if (sectionEl.__imageCardsResizeBound) return;
    sectionEl.__imageCardsResizeBound = true;

    window.addEventListener('resize', () => {
      const slider = sectionEl.querySelector('[data-image-cards-slider]');
      if (slider && slider.__imageCardsSwiper) {
        syncSlideWidth(sectionEl, slider.__imageCardsSwiper);
      }
    });
  }

  function initImageCardsSwiper(slider) {
    if (!slider || slider.hasAttribute(SWIPER_ATTR) || typeof window.Swiper === 'undefined') return;

    const sectionEl = slider.closest('[data-image-cards-section]') || slider.closest('.image-cards');
    if (!sectionEl) return;

    const { arrows, dots, autoplay, autoplaySpeed, infinite } = slider.dataset;
    const spaceBetween = parseInt(sectionEl.dataset.spaceBetween || '0', 10);

    const prev = sectionEl.querySelector('[data-image-cards-prev]');
    const next = sectionEl.querySelector('[data-image-cards-next]');
    const paginationEl = slider.querySelector('.image-cards__pagination');

    const config = {
      slidesPerView: 'auto',
      spaceBetween,
      loop: parseBool(infinite),
    };

    if (parseBool(arrows) && prev && next) {
      config.navigation = { prevEl: prev, nextEl: next };
    }

    if (parseBool(dots) && paginationEl) {
      config.pagination = { el: paginationEl, clickable: true };
    }

    if (parseBool(autoplay)) {
      config.autoplay = {
        delay: (parseInt(autoplaySpeed, 10) || 5) * 1000,
        disableOnInteraction: false,
      };
    }

    // eslint-disable-next-line no-new
    const swiper = new window.Swiper(slider, config);
    slider.__imageCardsSwiper = swiper;
    syncSlideWidth(sectionEl, swiper);
    bindSectionResize(sectionEl);
    slider.setAttribute(SWIPER_ATTR, 'true');
  }

  function initImageCardsSwipers(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-image-cards-slider]:not([' + SWIPER_ATTR + '])').forEach(initImageCardsSwiper);
  }

  function pauseIframe(iframe) {
    if (!iframe || !iframe.contentWindow) return;
    if (iframe.classList.contains('js-youtube')) {
      iframe.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      );
    } else if (iframe.classList.contains('js-vimeo')) {
      iframe.contentWindow.postMessage('{"method":"pause"}', '*');
    }
  }

  function resetImageCardDeferredMedia(deferredMedia) {
    if (!deferredMedia || deferredMedia.getAttribute('data-video-loop') === 'true') return;

    deferredMedia.querySelectorAll('video, iframe').forEach((node) => {
      if (node.closest('template')) return;
      if (node.tagName === 'VIDEO') {
        node.pause();
      } else {
        pauseIframe(node);
      }
      node.remove();
    });

    deferredMedia.removeAttribute('loaded');
  }

  function findDeferredMediaForIframe(sourceWindow) {
    const candidates = document.querySelectorAll(
      '.image-cards__deferred-media[data-video-loop="false"] iframe'
    );
    for (const iframe of candidates) {
      try {
        if (iframe.contentWindow === sourceWindow) {
          return iframe.closest('.image-cards__deferred-media');
        }
      } catch (e) {
        /* cross-origin */
      }
    }
    return null;
  }

  function handleIframeEnded(deferredMedia) {
    if (deferredMedia && deferredMedia.hasAttribute('loaded')) {
      resetImageCardDeferredMedia(deferredMedia);
    }
  }

  function onIframeMessage(event) {
    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    if (!data || typeof data !== 'object') return;

    const deferredMedia = findDeferredMediaForIframe(event.source);
    if (!deferredMedia) return;

    const host = deferredMedia.getAttribute('data-video-host');

    if (host === 'youtube') {
      if (data.event === 'onStateChange' && data.info === 0) {
        handleIframeEnded(deferredMedia);
      } else if (data.event === 'infoDelivery' && data.info && data.info.playerState === 0) {
        handleIframeEnded(deferredMedia);
      }
    } else if (host === 'vimeo' && (data.event === 'finish' || data.event === 'ended')) {
      handleIframeEnded(deferredMedia);
    }
  }

  function onVideoEnded(event) {
    const video = event.target;
    if (!video || video.tagName !== 'VIDEO') return;

    const deferredMedia = video.closest('.image-cards__deferred-media[data-video-loop="false"]');
    if (!deferredMedia || video.closest('template')) return;

    resetImageCardDeferredMedia(deferredMedia);
  }

  function bindImageCardsVideoReplay() {
    if (!window.__imageCardsVideoMessageBound) {
      window.addEventListener('message', onIframeMessage);
      document.addEventListener('ended', onVideoEnded, true);
      window.__imageCardsVideoMessageBound = true;
    }
  }

  function initImageCardsVideoReplay(root) {
    bindImageCardsVideoReplay();
    const scope = root || document;
    scope.querySelectorAll('.image-cards__deferred-media[data-video-loop="false"]').forEach((dm) => {
      const video = dm.querySelector('video:not(template video)');
      if (video && !video.dataset.imageCardsEndBound) {
        video.dataset.imageCardsEndBound = 'true';
        video.addEventListener('ended', () => resetImageCardDeferredMedia(dm));
      }
    });
  }

  function onReady(root) {
    initImageCardsSwipers(root);
    initImageCardsVideoReplay(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => onReady());
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    onReady(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyImageCardsSwipers(event.target);
  });
})();
